/**
 * Validation Service API Contracts
 * Request/Response schemas for the validation service
 */

import { z } from 'zod';

import { BooleanFromString } from '../common';

// ============================================================================
// Ticket Validation
// ============================================================================

export const ValidateTicketRequestSchema = z.object({
  qrCodeToken: z.string().max(100),
  gateId: z.string().min(1).max(50),
  validationType: z.enum(['entry', 'exit', 'attraction']).default('entry'),
  attractionId: z.string().min(1).max(50).optional(),
  deviceId: z.string().max(50).optional(),
});

export type ValidateTicketRequest = z.infer<typeof ValidateTicketRequestSchema>;

export const ValidationResultSchema = z.object({
  valid: z.boolean(),
  ticketId: z.string().uuid().optional(),
  ticketNumber: z.string().optional(),
  ticketType: z.string().optional(),
  visitorName: z.string().optional(),
  status: z.enum(['valid', 'invalid', 'expired', 'used', 'wrong_date', 'max_reentry', 'voided']),
  message: z.string(),
  entryCount: z.number().int().optional(),
  maxReentryCount: z.number().int().optional(),
  validUntil: z.string().datetime().optional(),
  validationTimeMs: z.number().int(),
  timestamp: z.string().datetime(),
});

export type ValidationResult = z.infer<typeof ValidationResultSchema>;

// ============================================================================
// Wristband Validation — INTERNAL USE BY validation-service ONLY
//
// These schemas are NOT a public endpoint contract. Per ADR-014, all scanner
// devices must call /api/v1/validate (or /api/v1/scan) on validation-service,
// which performs the HMAC check against the Redis cache (val:wristband:{code})
// and then calls wristband-service internally for credit deduction.
//
// There is NO public Kong route for /api/v1/wristband/validate. Any code
// outside of validation-service that imports these types is doing so
// incorrectly — use GateEntryRequestSchema / AttractionValidationRequestSchema
// for scanner-facing contracts instead.
// ============================================================================

export const ValidateWristbandRequestSchema = z.object({
  qrContent: z.string().max(50),
  gateId: z.string().max(50).optional(),
  attractionId: z.string().max(50).optional(),
  validationType: z.enum(['gate', 'attraction', 'purchase']).default('gate'),
  creditsRequired: z.number().int().nonnegative().optional(),
  deviceId: z.string().max(50).optional(),
});

export type ValidateWristbandRequest = z.infer<typeof ValidateWristbandRequestSchema>;

export const WristbandValidationResultSchema = z.object({
  valid: z.boolean(),
  wristbandId: z.string().uuid().optional(),
  wristbandCode: z.string().optional(),
  ticketId: z.string().uuid().optional(),
  customerId: z.string().min(1).optional(),
  visitorName: z.string().optional(),
  status: z.enum([
    'valid',
    'invalid',
    'expired',
    'voided',
    'insufficient_credits',
    'invalid_hmac',
    'counter_mismatch',
    'not_found',
  ]),
  message: z.string(),
  creditBalance: z.number().int().optional(),
  creditsDeducted: z.number().int().optional(),
  validUntil: z.string().datetime().optional(),
  validationTimeMs: z.number().int(),
  timestamp: z.string().datetime(),
});

export type WristbandValidationResult = z.infer<typeof WristbandValidationResultSchema>;

// ============================================================================
// Gate Management
// ============================================================================

export const GateTypeSchema = z.enum(['ENTRY', 'EXIT', 'REENTRY']);
export type GateType = z.infer<typeof GateTypeSchema>;

export const GateSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  type: GateTypeSchema,
  location: z.string().nullable(),
  isActive: z.boolean(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type Gate = z.infer<typeof GateSchema>;

export const GetGatesQuerySchema = z.object({
  type: GateTypeSchema.optional(),
  isActive: BooleanFromString.optional(),
});

export type GetGatesQuery = z.infer<typeof GetGatesQuerySchema>;

export const GateStatsSchema = z.object({
  gateId: z.string(),
  date: z.string(),
  totalScans: z.number().int(),
  successfulEntries: z.number().int(),
  failedAttempts: z.number().int(),
  successRate: z.string(),
});
export type GateStats = z.infer<typeof GateStatsSchema>;

export const GateStatusSchema = z.object({
  gateId: z.string(),
  gateName: z.string(),
  isOnline: z.boolean(),
  lastPing: z.string().datetime().nullable(),
  validationsToday: z.number().int(),
  currentQueueLength: z.number().int().nullable(),
  avgValidationTimeMs: z.number().int().nullable(),
  errorCount: z.number().int(),
});

export type GateStatus = z.infer<typeof GateStatusSchema>;

// ============================================================================
// Gate Entry / Exit — Phase 2 Scanner API
// ============================================================================

/**
 * GateEntryRequest — scanner device POST to /api/v1/validate/gate/entry
 * Auth: X-Scanner-API-Key header
 * SLA: <1s p95
 */
export const GateEntryRequestSchema = z.object({
  /** QR content from wristband scan (38-char HMAC-SHA256 format) */
  qrContent: z.string().min(1).max(50),
  /** Optional — service resolves gate from scanner registry via scannerId; not read from dto today */
  gateId: z.string().min(1).max(50).optional(),
  /** Scanner device ID; used for validation, logging, and gate resolution */
  scannerId: z.string().min(1).max(50),
  /** Millisecond timestamp on the scanner device (for latency diagnostics) */
  scanTimestampMs: z.number().int().optional(),
});

export type GateEntryRequest = z.infer<typeof GateEntryRequestSchema>;

export const GateEntryResponseSchema = z.object({
  allowed: z.boolean(),
  wristbandId: z.string().uuid().optional(),
  wristbandCode: z.string().optional(),
  customerId: z.string().optional(),
  visitorName: z.string().nullable().optional(),
  status: z.enum([
    'valid',
    'invalid',
    'already_inside',
    'expired',
    'voided',
    'invalid_hmac',
    'counter_mismatch',
    'not_found',
    'capacity_full',
  ]),
  message: z.string(),
  /** Server-computed round-trip validation time in milliseconds */
  validationTimeMs: z.number().int(),
  /** Display text for scanner screen (max 40 chars) */
  displayMessage: z.string().max(40),
  /** LED/sound instruction for scanner hardware */
  signal: z.enum(['green', 'red', 'yellow']),
  timestamp: z.string().datetime(),
});

export type GateEntryResponse = z.infer<typeof GateEntryResponseSchema>;

/**
 * GateExitRequest — scanner device POST to /api/v1/validate/gate/exit
 * Auth: X-Scanner-API-Key header
 */
export const GateExitRequestSchema = z.object({
  qrContent: z.string().min(1).max(50),
  /** Optional — service resolves gate from scanner registry via scannerId */
  gateId: z.string().min(1).max(50).optional(),
  scannerId: z.string().min(1).max(50),
  scanTimestampMs: z.number().int().optional(),
});

export type GateExitRequest = z.infer<typeof GateExitRequestSchema>;

export const GateExitResponseSchema = z.object({
  allowed: z.boolean(),
  wristbandId: z.string().uuid().optional(),
  wristbandCode: z.string().optional(),
  status: z.enum([
    'valid',
    'invalid',
    'not_inside',
    'expired',
    'voided',
    'invalid_hmac',
    'not_found',
  ]),
  message: z.string(),
  validationTimeMs: z.number().int(),
  displayMessage: z.string().max(40),
  signal: z.enum(['green', 'red', 'yellow']),
  timestamp: z.string().datetime(),
});

export type GateExitResponse = z.infer<typeof GateExitResponseSchema>;

export const GateReentryRequestSchema = z.object({
  qrContent: z.string().min(1).max(50),
  /** Optional — service resolves gate from scanner registry via scannerId */
  gateId: z.string().min(1).max(50).optional(),
  scannerId: z.string().min(1).max(50),
  scanTimestampMs: z.number().int().optional(),
});

export type GateReentryRequest = z.infer<typeof GateReentryRequestSchema>;

export const OVERRIDE_ACTIONS = ['FORCE_ENTRY', 'FORCE_EXIT', 'FORCE_REENTRY'] as const;
export type OverrideAction = typeof OVERRIDE_ACTIONS[number];

export const GateOverrideRequestSchema = z.object({
  qrContent: z.string().min(1).max(50),
  scannerId: z.string().max(50).optional(),
  action: z.enum(OVERRIDE_ACTIONS),
  staffId: z.string().min(1),
  reason: z.string().min(1),
  notes: z.string().optional(),
});

export type GateOverrideRequest = z.infer<typeof GateOverrideRequestSchema>;

// ============================================================================
// Attraction Validation — Phase 2 Scanner API
// ============================================================================

/**
 * AttractionValidationRequest — scanner device POST to /api/v1/validate/attraction
 * Auth: X-Scanner-API-Key header
 * SLA: <150ms p95 (performance critical)
 * Flow: 1) Verify HMAC   2) Check credit balance   3) Deduct credits (if required)
 */
export const AttractionValidationRequestSchema = z.object({
  wristbandCode: z.string().min(1).max(50),
  attractionCode: z.string().min(1).max(50),
  scannerId: z.string().max(50).optional(),
  scannerType: z.string().max(50).optional(),
});

export type AttractionValidationRequest = z.infer<typeof AttractionValidationRequestSchema>;

export const AttractionCheckOnlyRequestSchema = z.object({
  wristbandCode: z.string().min(1).max(50),
  attractionCode: z.string().min(1).max(50),
});

export type AttractionCheckOnlyRequest = z.infer<typeof AttractionCheckOnlyRequestSchema>;

export const AttractionOverrideRequestSchema = z.object({
  wristbandCode: z.string().min(1).max(50),
  attractionCode: z.string().min(1).max(50),
  scannerId: z.string().max(50).optional(),
  scannerType: z.string().max(50).optional(),
  staffId: z.string().min(1),
  overrideReason: z.string().min(1),
});

export type AttractionOverrideRequest = z.infer<typeof AttractionOverrideRequestSchema>;

export const AttractionExitRequestSchema = z.object({
  wristbandCode: z.string().min(1).max(50),
  attractionCode: z.string().min(1).max(50),
  scannerId: z.string().max(50).optional(),
});

export type AttractionExitRequest = z.infer<typeof AttractionExitRequestSchema>;

export const AttractionValidationResponseSchema = z.object({
  allowed: z.boolean(),
  wristbandId: z.string().uuid().optional(),
  wristbandCode: z.string().optional(),
  customerId: z.string().optional(),
  visitorName: z.string().nullable().optional(),
  status: z.enum([
    'valid',
    'insufficient_credits',
    'invalid',
    'expired',
    'voided',
    'invalid_hmac',
    'counter_mismatch',
    'not_found',
    'attraction_closed',
    'already_used_today',
  ]),
  message: z.string(),
  /** Credits deducted in this transaction (0 if dryRun=true or creditsRequired=0) */
  creditsDeducted: z.number().int().nonnegative().optional(),
  /** Remaining credit balance after deduction */
  remainingCredits: z.number().int().nonnegative().optional(),
  /** Points awarded for this attraction visit (sent to gamification-svc async) */
  pointsAwarded: z.number().int().nonnegative().optional(),
  validationTimeMs: z.number().int(),
  displayMessage: z.string().max(40),
  signal: z.enum(['green', 'red', 'yellow']),
  timestamp: z.string().datetime(),
});

export type AttractionValidationResponse = z.infer<typeof AttractionValidationResponseSchema>;

// ============================================================================
// Validation Statistics
// ============================================================================

export const ValidationStatsSchema = z.object({
  date: z.string(),
  totalValidations: z.number().int(),
  successfulValidations: z.number().int(),
  failedValidations: z.number().int(),
  avgValidationTimeMs: z.number().int(),
  p95ValidationTimeMs: z.number().int(),
  byGate: z.array(z.object({
    gateId: z.string(),
    gateName: z.string(),
    entries: z.number().int(),
    exits: z.number().int(),
    failures: z.number().int(),
  })),
  byHour: z.array(z.object({
    hour: z.number().int(),
    entries: z.number().int(),
    exits: z.number().int(),
  })),
});

export type ValidationStats = z.infer<typeof ValidationStatsSchema>;

export const GetValidationStatsQuerySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  gateId: z.string().optional(),
});

export type GetValidationStatsQuery = z.infer<typeof GetValidationStatsQuerySchema>;

// ============================================================================
// Scanner Device Registry — Request Schemas
// ============================================================================

export const ScannerDeviceTypeSchema = z.enum([
  'GATE_SCANNER',
  'ATTRACTION_SCANNER',
  'KIOSK',
  'POS_TERMINAL',
]);
export type ScannerDeviceType = z.infer<typeof ScannerDeviceTypeSchema>;

export const ScannerDeviceStatusSchema = z.enum([
  'ONLINE',
  'OFFLINE',
  'MAINTENANCE',
  'ERROR',
  'DECOMMISSIONED',
]);
export type ScannerDeviceStatus = z.infer<typeof ScannerDeviceStatusSchema>;

export const ScannerLocationTypeSchema = z.enum(['GATE', 'ATTRACTION']);
export type ScannerLocationType = z.infer<typeof ScannerLocationTypeSchema>;

/**
 * Lane a fixed turnstile scanner serves (#716). `null` for bidirectional /
 * handheld scanners that choose direction per-scan; IN/OUT for turnstiles bound
 * to one lane.
 *
 * INVARIANT: this is **advisory grouping metadata only**. It is NOT validated
 * against the scan flow, which picks direction per-scan via separate endpoints
 * using ENTRY/EXIT vocabulary. The correspondence is IN ↔ ENTRY, OUT ↔ EXIT;
 * any code that bridges the registry lane to a scan direction must map it
 * explicitly here — nothing enforces that a `direction=IN` scanner only ever
 * hits the enter endpoint.
 */
export const ScannerDirectionSchema = z.enum(['IN', 'OUT']);
export type ScannerDirection = z.infer<typeof ScannerDirectionSchema>;

export const ScannerSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: ScannerDeviceTypeSchema,
  status: ScannerDeviceStatusSchema,
  locationId: z.string().nullable(),
  // Only GATE/ATTRACTION are ever written (via AssignScannerSchema); tightened
  // from a loose string so consumers can trust the value (#716).
  locationType: ScannerLocationTypeSchema.nullable(),
  direction: ScannerDirectionSchema.nullable(),
  lastPingAt: z.string().nullable(),
  firmware: z.string().nullable(),
  isOnline: z.boolean(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string().optional(),
  apiKey: z.string().optional(),
});
export type ScannerResponse = z.infer<typeof ScannerSchema>;

export const RegisterScannerSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  type: ScannerDeviceTypeSchema,
  locationId: z.string().optional(),
  locationType: ScannerLocationTypeSchema.optional(),
  direction: ScannerDirectionSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type RegisterScannerRequest = z.infer<typeof RegisterScannerSchema>;

export const AssignScannerSchema = z.object({
  locationId: z.string().min(1),
  locationType: ScannerLocationTypeSchema,
  direction: ScannerDirectionSchema.optional(),
});
export type AssignScannerRequest = z.infer<typeof AssignScannerSchema>;

export const ScannerHeartbeatSchema = z.object({
  firmware: z.string().optional(),
  batteryPercent: z.number().optional(),
  selfStatus: z.string().optional(),
});
export type ScannerHeartbeatRequest = z.infer<typeof ScannerHeartbeatSchema>;

/**
 * Query for GET /api/v1/scanners?type=&status=&locationId=&direction= — used by
 * the admin attraction-centric turnstile view (#716) to scope scanners to one
 * attraction and lane. All filters optional; unknown enum values rejected.
 */
export const GetScannersQuerySchema = z.object({
  type: ScannerDeviceTypeSchema.optional(),
  status: ScannerDeviceStatusSchema.optional(),
  locationId: z.string().min(1).max(50).optional(),
  direction: ScannerDirectionSchema.optional(),
});
export type GetScannersQuery = z.infer<typeof GetScannersQuerySchema>;

// ============================================================================
// Attractions — handheld post picker (#529 / S3)
//
// A validation-service-scoped view of an attraction: exactly the fields the
// handheld operator needs to pick and staff a post. Deliberately NOT ticketing's
// `AttractionSchema` (the far richer source-of-truth shape) — this reflects the
// synced `attractions` table validation-service serves.
//
// The enums are declared LOCALLY (not imported from ticketing) so this domain
// stays self-contained — a cross-domain source import created a circular-load
// TDZ in dev. Values mirror validation-service's own Prisma enums
// (schema-db-alignment); keep them in sync if the DB enums change.
// ============================================================================

const OperableAttractionCategorySchema = z.enum([
  'thrill',
  'family',
  'kids',
  'water',
  'show',
  'experience',
]);
const OperableAttractionAccessTypeSchema = z.enum(['CREDIT', 'TICKET', 'FREE']);
const OperableAttractionStatusSchema = z.enum([
  'OPERATIONAL',
  'MAINTENANCE',
  'CLOSED',
  'WEATHER_CLOSURE',
  'TECHNICAL_ISSUE',
  'STAFF_SHORTAGE',
]);

export const OperableAttractionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: OperableAttractionCategorySchema,
  accessType: OperableAttractionAccessTypeSchema,
  status: OperableAttractionStatusSchema,
  creditCost: z.number().int().nonnegative(),
  requiresCredit: z.boolean(),
  isActive: z.boolean(),
});
export type OperableAttraction = z.infer<typeof OperableAttractionSchema>;

/** Response payload (envelope-unwrapped) of GET /api/v1/attractions. */
export const OperableAttractionListSchema = z.array(OperableAttractionSchema);
export type OperableAttractionList = z.infer<typeof OperableAttractionListSchema>;

/** Query for GET /api/v1/attractions?status= — optional status filter. */
export const GetOperableAttractionsQuerySchema = z.object({
  status: OperableAttractionStatusSchema.optional(),
});
export type GetOperableAttractionsQuery = z.infer<typeof GetOperableAttractionsQuerySchema>;

/** Response payload (envelope-unwrapped) of GET /api/v1/scanners — a list of posts. */
export const ScannerListSchema = z.array(ScannerSchema);
export type ScannerList = z.infer<typeof ScannerListSchema>;

// ============================================================================
// Blacklist — Request Schemas
// ============================================================================

export const AddToBlacklistSchema = z.object({
  wristbandCode: z.string().min(1).max(50),
  wristbandId: z.string().max(50).optional(),
  reason: z.string().min(1).max(500),
});
export type AddToBlacklistRequest = z.infer<typeof AddToBlacklistSchema>;

// ============================================================================
// Offline Sync — Request Schema
// ============================================================================

export const OfflineValidationActionSchema = z.enum([
  'GATE_ENTRY',
  'GATE_EXIT',
  'GATE_REENTRY',
  'ATTRACTION_SCAN',
]);

export const OfflineValidationResultSchema = z.enum(['ALLOWED', 'DENIED']);

const offlineValidationCommonFields = {
  result: OfflineValidationResultSchema,
  timestamp: z.string().datetime(),
  counterValue: z.number().int().min(0).optional(),
  failureReason: z.string().optional(),
};

function refineGateOfflineIdentity(
  entry: { ticketId?: string; wristbandCode?: string },
  ctx: z.RefinementCtx,
) {
  if (!entry.ticketId && !entry.wristbandCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Gate offline entries require ticketId or wristbandCode',
      path: ['ticketId'],
    });
  }
}

const gateOfflineEntrySchema = z
  .object({
    action: z.enum(['GATE_ENTRY', 'GATE_EXIT', 'GATE_REENTRY']),
    ticketId: z.string().optional(),
    wristbandCode: z.string().optional(),
    gateId: z.string().optional(),
    ...offlineValidationCommonFields,
  })
  .superRefine(refineGateOfflineIdentity);

const attractionOfflineEntrySchema = z.object({
  action: z.literal('ATTRACTION_SCAN'),
  wristbandCode: z.string().min(1),
  attractionId: z.string().min(1),
  ...offlineValidationCommonFields,
});

/** Gate entries (gateId) vs attraction entries (attractionId) — separate union branches. */
export const OfflineValidationEntrySchema = z.union([
  gateOfflineEntrySchema,
  attractionOfflineEntrySchema,
]);

export const OfflineSyncRequestSchema = z.object({
  scannerId: z.string().min(1),
  validations: z.array(OfflineValidationEntrySchema),
});
export type OfflineSyncRequest = z.infer<typeof OfflineSyncRequestSchema>;

export * from './gate';
